/** SUNTRONIX ONE — SALES DESK.
 *
 *  Route: /sales-desk
 *  Purpose: action-first workspace for the internal Sales / Commercial
 *  team.  The most visible action is + NEW QUOTATION.  Every list is
 *  hydrated from the real /api/sales-desk/home aggregate.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { customerPartNumber } from "@/lib/partNumber";
import MyWorkPanel from "@/pages/sales/MyWorkPanel";
import { SalesDeskFollowUpWidget } from "@/pages/commercial/FollowUp";
import { WinAnalyticsPanel, QuoteNudgesPanel } from "@/pages/sales/WinAnalytics";
import MyApprovals from "@/pages/approvals/MyApprovals";
import {
  Plus, Search, FileText, Users, Package, ShoppingBag, ClipboardList,
  Clock, ThumbsUp, Inbox, ChevronRight, RefreshCw,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const fmtWhen = (v) => v ? new Date(v).toLocaleDateString(undefined,
  { day: "numeric", month: "short", year: "numeric" }) : "—";

export default function SalesDesk() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [customerQ, setCustomerQ] = useState("");
  const [productQ, setProductQ] = useState("");
  const [custHits, setCustHits] = useState([]);
  const [prodHits, setProdHits] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sales-desk/home?limit=8");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Live customer / product search inside the command bar
  useEffect(() => {
    if (!customerQ.trim()) { setCustHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/vtiger/crm-customers?q=${encodeURIComponent(customerQ)}&limit=6`);
        setCustHits(data.items || []);
      } catch { /* ignore */ }
    }, 220);
    return () => clearTimeout(t);
  }, [customerQ]);

  useEffect(() => {
    if (!productQ.trim()) { setProdHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/product-master/products?q=${encodeURIComponent(productQ)}&page_size=6`);
        setProdHits(data.items || []);
      } catch { /* ignore */ }
    }, 220);
    return () => clearTimeout(t);
  }, [productQ]);

  const newQuote = () => navigate("/commercial/crm/quotations?new=1");

  return (
    <div className="space-y-6 max-w-[1500px]" data-testid="sales-desk">
      <MyWorkPanel />

      {/* ================================================================
        *  MY APPROVALS — action-first
        * ================================================================ */}
      <div className="rounded-lg border border-slate-200 bg-white p-4"
          data-testid="sales-desk-approvals">
        <MyApprovals compact />
      </div>

      {/* ================================================================
        *  FOLLOW UP TODAY — action-first
        * ================================================================ */}
      <div className="rounded-lg border border-slate-200 bg-white p-4"
          data-testid="sales-desk-followup">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
            Follow Up Today
          </div>
          <Link to="/commercial/followup"
              className="text-[11px] text-emerald-800 hover:underline ml-auto">
            Open full view →
          </Link>
        </div>
        <SalesDeskFollowUpWidget />
      </div>

      {/* Unopened issued quotes — 3+ day nudges */}
      <QuoteNudgesPanel />

      {/* Quote win analytics from imported history */}
      <div className="rounded-lg border border-slate-200 bg-white p-4"
          data-testid="sales-desk-win-analytics">
        <WinAnalyticsPanel />
      </div>
      {/* ================================================================
        *  HERO — the ONE action that matters most: New Quotation
        * ================================================================ */}
      <div className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-5"
        data-testid="sales-desk-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
              Sales Desk
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-slate-900 mt-1">
              Prepare a quotation in under a minute.
            </h1>
            <div className="text-[13px] text-slate-600 mt-1.5 max-w-xl">
              Everything you need to draft, price and issue a customer quote —
              connected to your existing Customer Master and Product Master.
            </div>
          </div>
          <button
            onClick={newQuote}
            data-testid="sales-desk-new-quote-hero"
            className="h-12 px-6 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-[15px] font-semibold flex items-center gap-2 shadow-md">
            <Plus className="h-5 w-5" strokeWidth={2.4} /> New Quotation
          </button>
        </div>

        {/* Quotation Command Bar */}
        <div className="mt-5 flex flex-wrap items-center gap-2"
          data-testid="sales-desk-command-bar">
          <QuickAction icon={FileText} label="Quotations"
            testId="sd-cmd-quotations"
            onClick={() => navigate("/commercial/crm/quotations")} />
          <QuickAction icon={Inbox} label="Customer POs"
            testId="sd-cmd-cpo"
            onClick={() => navigate("/commercial/customer-po")} />
          <QuickAction icon={ClipboardList} label="Orders"
            testId="sd-cmd-orders"
            onClick={() => navigate("/commercial/one-orders")} />
          <QuickAction icon={Users} label="Customers"
            testId="sd-cmd-customers"
            onClick={() => navigate("/companies")} />
          <QuickAction icon={Package} label="Products"
            testId="sd-cmd-products"
            onClick={() => navigate("/technology/product-master")} />
          <div className="flex-1" />
          <button
            onClick={load} disabled={busy}
            data-testid="sales-desk-refresh"
            className="h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-50 text-[12px] text-slate-700 flex items-center gap-1.5">
            <RefreshCw className={"h-3.5 w-3.5 " + (busy ? "animate-spin" : "")} />
            Refresh
          </button>
        </div>

        {/* Instant search — customer + product */}
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <SearchBox icon={Search} placeholder="Search customer by name, code, GSTIN, city…"
            value={customerQ} onChange={setCustomerQ}
            testId="sd-search-customer">
            {custHits.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                {custHits.map((c) => (
                  <button key={c.id}
                    data-testid={`sd-hit-customer-${c.id}`}
                    onClick={() => {
                      setCustomerQ(""); setCustHits([]);
                      navigate(`/commercial/crm/quotations?new=1&customer_id=${c.id}`);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-[13px] flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-emerald-700" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.account_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {c.customer_code || c.code} · {c.billing_city || c.city || "—"}
                      </div>
                    </div>
                    <Plus className="h-3.5 w-3.5 text-emerald-700" />
                  </button>
                ))}
              </div>
            )}
          </SearchBox>
          <SearchBox icon={Search} placeholder="Search product by name, SKU, part number, brand…"
            value={productQ} onChange={setProductQ}
            testId="sd-search-product">
            {prodHits.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                {prodHits.map((p) => (
                  <button key={p.id}
                    data-testid={`sd-hit-product-${p.id}`}
                    onClick={() => {
                      setProductQ(""); setProdHits([]);
                      navigate(`/technology/product-master?product=${p.id}`);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-[13px] flex items-center gap-2">
                    <Package className="h-3.5 w-3.5 text-emerald-700" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{p.product_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {p.brand || "—"} · {customerPartNumber(p)}
                      </div>
                    </div>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                ))}
              </div>
            )}
          </SearchBox>
        </div>
      </div>

      {/* ================================================================
        *  WORK LISTS — action rather than analytics
        * ================================================================ */}
      <div className="grid md:grid-cols-2 xl:grid-cols-2 gap-4"
        data-testid="sales-desk-lists">
        <ListCard title="Drafts" icon={FileText} testId="sd-list-drafts"
          rows={data?.drafts} kind="quote"
          empty="No drafts. Start a new quotation to see it here."
          openTo="/commercial/crm/quotations" />
        <ListCard title="Awaiting customer response"
          icon={Clock} testId="sd-list-awaiting"
          rows={data?.awaiting_response} kind="quote"
          empty="Nothing waiting on customers."
          openTo="/commercial/crm/quotations" />
        <ListCard title="Accepted quotes" icon={ThumbsUp}
          testId="sd-list-accepted"
          rows={data?.accepted_quotes} kind="quote"
          empty="No accepted quotes yet." tone="ok"
          openTo="/commercial/crm/quotations" />
        <ListCard title="Customer POs received" icon={Inbox}
          testId="sd-list-cpo"
          rows={data?.customer_po_received} kind="cpo"
          empty="No new Customer POs."
          openTo="/commercial/customer-po" />
        <ListCard title="Recent quotations" icon={FileText}
          testId="sd-list-recent-quotes"
          rows={data?.recent_quotations} kind="quote"
          empty="No quotations yet."
          openTo="/commercial/crm/quotations" />
        <ListCard title="Recent customers" icon={Users}
          testId="sd-list-recent-customers"
          rows={data?.recent_customers} kind="customer"
          empty="No customers indexed yet."
          openTo="/companies" />
        <ListCard title="Recent products" icon={Package}
          testId="sd-list-recent-products"
          rows={data?.recent_products} kind="product"
          empty="Product master empty."
          openTo="/technology/product-master" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function QuickAction({ icon: Icon, label, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="h-9 px-3 rounded-md border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-[12px] text-slate-700 flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-emerald-700" />
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ */
function SearchBox({ icon: Icon, placeholder, value, onChange, children, testId }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white focus-within:border-emerald-400 focus-within:ring-2 focus-within:ring-emerald-100">
        <Icon className="h-4 w-4 text-slate-400" />
        <input value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className="flex-1 outline-none text-[13px] bg-transparent" />
        {value && (
          <button onClick={() => onChange("")} className="text-slate-400 text-[11px]">
            clear
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ListCard({ title, icon: Icon, rows, kind, empty, openTo, tone, testId }) {
  const border = tone === "ok" ? "border-emerald-200" : "border-slate-200";
  return (
    <div className={"border rounded-lg bg-white " + border}
      data-testid={testId}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
        <div className="flex items-center gap-2 text-[13px] font-medium text-slate-800">
          <Icon className="h-3.5 w-3.5 text-emerald-700" />
          {title}
          <span className="text-slate-400 text-[11px] ml-1">
            {rows?.length ?? 0}
          </span>
        </div>
        {openTo && (
          <Link to={openTo}
            className="text-[11px] text-emerald-700 hover:underline flex items-center gap-0.5">
            Open all <ChevronRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {(!rows || rows.length === 0) && (
          <div className="px-4 py-6 text-center text-[12px] text-slate-500 italic">
            {empty}
          </div>
        )}
        {(rows || []).map((r) => (
          <ListRow key={r.id} r={r} kind={kind} />
        ))}
      </div>
    </div>
  );
}

function ListRow({ r, kind }) {
  const navigate = useNavigate();
  const openQuote = (q) => navigate(`/commercial/crm/quotations`);
  const openCustomer = (c) => navigate(`/companies?q=${encodeURIComponent(c.account_name || "")}`);
  const openProduct = (p) => navigate(`/technology/product-master/${p.id}`);
  const openCpo = () => navigate(`/commercial/customer-po`);

  if (kind === "quote") return (
    <button className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
      onClick={() => openQuote(r)}
      data-testid={`sd-row-quote-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] font-semibold text-slate-900">
            {r.code}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {r.status}
          </span>
          {r.acceptance_status === "ACCEPTED" && (
            <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5">
              accepted
            </span>
          )}
        </div>
        <div className="text-[12px] text-slate-700 truncate mt-0.5">
          {r.customer_name || "—"} · {r.subject || "—"}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">
          {fmtWhen(r.updated_at || r.created_at)}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-[13px] font-semibold text-slate-900">
          {fmtINR(r.grand_total_inr)}
        </div>
      </div>
    </button>
  );

  if (kind === "cpo") return (
    <button className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
      onClick={openCpo} data-testid={`sd-row-cpo-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] font-semibold text-slate-900">
            {r.customer_po_number || r.code}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {r.status}
          </span>
        </div>
        <div className="text-[12px] text-slate-700 truncate mt-0.5">
          {r.customer_name || "—"}{r.quotation_code && <> · QUO {r.quotation_code}</>}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">{fmtWhen(r.created_at)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-[13px] font-semibold text-slate-900">
          {fmtINR(r.total_amount_inr)}
        </div>
      </div>
    </button>
  );

  if (kind === "customer") return (
    <button className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
      onClick={() => openCustomer(r)}
      data-testid={`sd-row-customer-${r.id}`}>
      <Users className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-800 truncate">
          {r.account_name}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {r.customer_code || "—"} · {r.billing_city || "—"} · {r.gstin || "no GSTIN"}
        </div>
      </div>
      <Link
        to={`/commercial/crm/quotations?new=1&customer_id=${r.id}`}
        onClick={(e) => e.stopPropagation()}
        className="text-[11px] text-emerald-700 hover:underline"
        data-testid={`sd-quote-for-${r.id}`}>
        + Quote
      </Link>
    </button>
  );

  if (kind === "product") return (
    <button className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3"
      onClick={() => openProduct(r)}
      data-testid={`sd-row-product-${r.id}`}>
      <Package className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-800 truncate">
          {r.product_name}
        </div>
        <div className="text-[11px] text-slate-500 truncate">
          {r.brand || "—"} · {customerPartNumber(r)}
        </div>
      </div>
      <div className="text-[12px] font-mono text-slate-700 shrink-0">
        {r.current_selling_price
          ? fmtINR(r.current_selling_price) : "—"}
      </div>
    </button>
  );

  return null;
}
